import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { addDays, formatDate, mondayOf, weekdayOf } from "@/lib/services";
import { useServices } from "@/context/ServicesContext";
import { RouteStop } from "@/components/app/RouteStop";

const dayRouteUrl = (stops) => {
  const addrs = stops.map((s) => s.address).filter(Boolean);
  if (!addrs.length) return null;
  const dest = encodeURIComponent(addrs[addrs.length - 1]);
  const wp = addrs.slice(0, -1).map(encodeURIComponent).join("|");
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}${wp ? `&waypoints=${wp}` : ""}`;
};

const DayBlock = ({ iso, stops, isToday }) => {
  const url = dayRouteUrl(stops);
  return (
    <div data-testid={`route-day-${iso}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <span className={isToday ? "text-slate-50" : "text-slate-300"}>{weekdayOf(iso)}</span>
          <span className="font-mono text-xs text-slate-500">{formatDate(iso)}</span>
          {isToday && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-900">HOJE</span>}
        </div>
        {url && (
          <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs font-medium text-indigo-300 hover:underline" data-testid={`route-maps-${iso}`}>
            <Navigation className="h-3.5 w-3.5" /> Rota no Maps
          </a>
        )}
      </div>
      {stops.length === 0 ? <p className="rounded-lg border border-dashed border-slate-800 py-3 text-center text-xs text-slate-600">Sem visitas</p> : (
        <ol className="space-y-2">{stops.map((s, i) => <RouteStop key={s.id} s={s} n={i + 1} />)}</ol>
      )}
    </div>
  );
};

export const WeekRoute = ({ version }) => {
  const { today } = useServices();
  const [start, setStart] = useState(mondayOf(today));
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/route", { params: { start, days: 7, today } }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [start, today, version]);

  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));

  return (
    <section data-testid="week-route">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-bold">
          Rota da semana <span className="font-mono text-sm text-slate-500" data-testid="week-range">{formatDate(start)} – {formatDate(addDays(start, 6))}</span>
        </h2>
        <div className="flex items-center gap-1">
          <Button size="icon" variant="ghost" onClick={() => setStart(addDays(start, -7))} data-testid="week-prev-button" aria-label="Semana anterior"><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" className="border-slate-700 bg-transparent" onClick={() => setStart(mondayOf(today))} data-testid="week-today-button">Esta semana</Button>
          <Button size="icon" variant="ghost" onClick={() => setStart(addDays(start, 7))} data-testid="week-next-button" aria-label="Próxima semana"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      <p className="mb-5 text-sm text-slate-400" data-testid="week-total">{data ? `${data.items.length} visita(s) agendada(s) nesta semana` : "Carregando…"}</p>
      {data?.overdue.length > 0 && (
        <div className="mb-8 rounded-xl border border-rose-500/30 bg-rose-500/5 p-4" data-testid="route-overdue">
          <div className="mb-3 text-sm font-semibold text-rose-300">Atrasados — ainda precisam de visita ({data.overdue.length})</div>
          <ol className="space-y-2">{data.overdue.map((s, i) => <RouteStop key={s.id} s={s} n={i + 1} showDate />)}</ol>
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        {days.map((iso) => (
          <DayBlock key={iso} iso={iso} isToday={iso === today} stops={(data?.items || []).filter((s) => s.execution_date === iso)} />
        ))}
      </div>
    </section>
  );
};
